import {FC, useState} from 'react'
import {useController, useFormContext} from 'react-hook-form'
import {MasterFinder} from 'components/ui/MasterFinder'
import {FormField, FieldBaseProps} from 'components/forms/FormField'
import {Label, Required} from 'jbc-front/components/presenters/form/Label'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {Note} from 'jbc-front/components/presenters/ui/Note'
import {useSearchOffices} from 'features/offices/OfficeSelector/useSearchOffices'
import {Spacer} from 'jbc-front/components/presenters/Spacer'

const PER_PAGE = 10

type OfficeCheckboxFieldProps = FieldBaseProps & {
  name: string
  filterByCurrentOffices: boolean
  disabled?: boolean
}

export const OfficeCheckboxField: FC<OfficeCheckboxFieldProps> = ({
  filterByCurrentOffices,
  name,
  label,
  note,
  required = false,
  error,
  disabled,
}) => {
  const {control} = useFormContext()
  const {field} = useController({
    name,
    control,
  })

  const [searchKeyword, setSearchKeyword] = useState<string | undefined>(undefined)
  const [currentPage, setCurrentPage] = useState(1)

  const selectedIds = (field.value ?? []) as string[]

  const {offices, totalCount} = useSearchOffices(currentPage, PER_PAGE, searchKeyword, filterByCurrentOffices, false)

  const searchResults = offices?.map((office) => ({
    key: String(office.id),
    label: String(office.name),
    selected: selectedIds.includes(String(office.id)),
  }))

  const handleSearch = (keyword: string | undefined, page: number) => {
    setSearchKeyword(keyword)
    setCurrentPage(page)
  }

  const handleSelect = (item: {key: string; label: string}) => {
    if (disabled) return
    const next = selectedIds.includes(item.key)
      ? selectedIds.filter((id) => id !== item.key)
      : [...selectedIds, item.key]
    field.onChange(next)
    field.onBlur()
  }

  const handleClear = () => {
    if (disabled) return
    field.onChange([])
    field.onBlur()
  }

  return (
    <FormField>
      {label && (
        <FormField.LabelContainer>
          <Label>
            {label}
            {required && (
              <>
                <Spacer direction="x" size={8} />
                <Required />
              </>
            )}
          </Label>
        </FormField.LabelContainer>
      )}

      <div className={disabled ? 'is-disabled' : undefined} style={{pointerEvents: disabled ? 'none' : undefined}}>
        <MasterFinder
          multiple
          searchResults={searchResults}
          totalResult={totalCount}
          currentPage={currentPage}
          perPage={PER_PAGE}
          onSearch={handleSearch}
          onSelect={handleSelect}
          onClear={handleClear}
        />
      </div>

      {note && (
        <FormField.NoteContainer>
          <Note>{note}</Note>
        </FormField.NoteContainer>
      )}

      {error && (
        <FormField.ErrorContainer>
          <ErrorMessage>{error}</ErrorMessage>
        </FormField.ErrorContainer>
      )}
    </FormField>
  )
}
